import { ContextMenuSurface } from '@src/components/common/contextMenuStyles';
import { ABOVE_ANCHORED_POPOVER_Z } from '@src/components/common/topLayerZ';
import { useAnchoredDropdown } from '@src/components/common/useAnchoredDropdown';
import { MARKER_KINDS } from '@src/services/dubbing/directionSupport';
import SearchIconOutline from '@src/svgs/SearchIconOutline';
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

import { DUB_DIRECTION_PORTAL_ATTR } from './dubDirectionPortal';

import type { MarkerDeclaration } from '@src/services/dubbing/directionSupport';
import type { KeyboardEvent, RefObject } from 'react';

export interface AudioTagPickerProps {
  open: boolean;
  /** The control the picker hangs off: a split button's chevron, a slot between two words. */
  anchorRef: RefObject<HTMLElement>;
  /** The tags the run's voices document, folded in `directionSupport.ts`. */
  markers: readonly MarkerDeclaration[];
  onPick: (tag: string) => void;
  onClose: () => void;
}

export const PICKER_WIDTH = 300;
const PICKER_MAX_HEIGHT = 360;

/**
 * PLAT-12032 — the audio tag picker: a search field, then the documented
 * tags as gold chips under one quiet heading per kind, filtered as you type
 * and walked with the keyboard. Opened from the Auto-tag split button's
 * chevron and from a slot between two words (PLAT-12079); it replaces the
 * one-row-per-tag list of PLAT-12023.
 *
 * Portaled to the body on `useAnchoredDropdown` at the tier above the Redub
 * Selection popover (PLAT-12049), and marked with the direction portal
 * attribute so the popover's click-away leaves it alone (PLAT-12074). The
 * hook owns dismissal: click outside, scroll, resize, Escape.
 *
 * Keyboard: the search field has focus on open; ArrowDown leaves it for the
 * first chip, the arrows walk the visible chips in reading order, Enter or
 * Space picks, Escape closes, and typing from a chip returns to the field.
 */
export interface AudioTagPanelProps {
  markers: readonly MarkerDeclaration[];
  onPick: (tag: string) => void;
}

/**
 * The picker's content on its own: the search row and the chips. Mounted by
 * `AudioTagPicker` in its own portal, and by the Auto-tag split button
 * inside the menu it already portals (PLAT-12079). Focus lands in the search
 * on mount.
 */
export const AudioTagPanel = ({ markers, onPick }: AudioTagPanelProps) => {
  const [query, setQuery] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);
  const chipRefs = useRef<(HTMLButtonElement | null)[]>([]);

  useEffect(() => {
    const id = window.requestAnimationFrame(() => searchRef.current?.focus());
    return () => window.cancelAnimationFrame(id);
  }, []);

  const needle = query.trim().toLowerCase();
  const groups = useMemo(
    () =>
      MARKER_KINDS.map(group => ({
        ...group,
        markers: markers.filter(
          m => m.kind === group.kind && (!needle || m.tag.includes(needle)),
        ),
      })).filter(group => group.markers.length > 0),
    [markers, needle],
  );
  // The chips in reading order, for the arrow keys.
  const visible = groups.flatMap(group => group.markers.map(m => m.tag));
  chipRefs.current.length = visible.length;

  const focusChip = (index: number) => {
    if (visible.length === 0) return;
    const wrapped = (index + visible.length) % visible.length;
    chipRefs.current[wrapped]?.focus();
  };

  const onSearchKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      focusChip(0);
    } else if (e.key === 'Enter' && visible.length > 0) {
      // Enter in the field picks the first match: "type, Enter" is the
      // fast path the ticket asks for.
      e.preventDefault();
      onPick(visible[0]);
    }
  };

  const onChipKeyDown = (index: number) => (e: KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        e.preventDefault();
        focusChip(index + 1);
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        e.preventDefault();
        focusChip(index - 1);
        break;
      case 'Home':
        e.preventDefault();
        focusChip(0);
        break;
      case 'End':
        e.preventDefault();
        focusChip(visible.length - 1);
        break;
      default:
        // A printable key from a chip goes on searching.
        if (e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey) {
          searchRef.current?.focus();
        }
    }
  };

  let chipIndex = -1;
  return (
    <>
      <SearchRow>
        <SearchIconOutline width={14} height={14} />
        <SearchInput
          ref={searchRef}
          type="text"
          value={query}
          placeholder="Search tags"
          aria-label="Search tags"
          onChange={e => setQuery(e.target.value)}
          onKeyDown={onSearchKeyDown}
          data-testid="dub-direction-marker-search"
        />
      </SearchRow>
      <Groups>
        {groups.length === 0 && (
          <EmptyLine data-testid="dub-direction-marker-empty">
            No tag matches “{query.trim()}”.
          </EmptyLine>
        )}
        {groups.map(group => (
          <Group key={group.kind}>
            <GroupLabel>{group.label}</GroupLabel>
            <ChipRow>
              {group.markers.map(m => {
                chipIndex += 1;
                const index = chipIndex;
                return (
                  <TagChip
                    key={m.tag}
                    ref={el => {
                      chipRefs.current[index] = el;
                    }}
                    type="button"
                    role="option"
                    aria-selected={false}
                    tabIndex={-1}
                    onClick={() => onPick(m.tag)}
                    onKeyDown={onChipKeyDown(index)}
                  >
                    [{m.tag}]
                  </TagChip>
                );
              })}
            </ChipRow>
          </Group>
        ))}
      </Groups>
    </>
  );
};

const AudioTagPicker = ({
  open,
  anchorRef,
  markers,
  onPick,
  onClose,
}: AudioTagPickerProps) => {
  const { menuRef, style } = useAnchoredDropdown(
    open,
    anchorRef,
    onClose,
    PICKER_MAX_HEIGHT,
    { width: PICKER_WIDTH, zIndex: ABOVE_ANCHORED_POPOVER_Z },
  );

  if (!open || !style) return null;

  return createPortal(
    <Surface
      ref={menuRef}
      style={style}
      role="listbox"
      aria-label="Audio tags"
      data-testid="dub-direction-marker-menu"
      {...{ [DUB_DIRECTION_PORTAL_ATTR]: '' }}
    >
      <AudioTagPanel markers={markers} onPick={onPick} />
    </Surface>,
    document.body,
  );
};

export default AudioTagPicker;

// The shared floating-menu surface, as a column: the search row pinned, the
// groups scrolling under it past the cap the anchor hook sets.
const Surface = styled(ContextMenuSurface)`
  gap: 0;
  padding: var(--spacing-xs);
  overflow: hidden;
`;

// The design system's in-menu search input (Filter Pills, Font Family
// dropdown): 30px, bordered, the glyph inside at the left.
const SearchRow = styled.div`
  position: relative;
  flex-shrink: 0;
  margin-bottom: 6px;
  color: var(--text-muted);

  svg {
    position: absolute;
    left: 9px;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
  }
`;

const SearchInput = styled.input`
  box-sizing: border-box;
  width: 100%;
  height: 30px;
  padding: 0 10px 0 28px;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: transparent;
  color: var(--foreground);
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  outline: none;
  transition: border-color var(--transition-fast);

  &::placeholder {
    color: var(--text-muted);
  }
  &:hover {
    border-color: var(--border-strong, var(--text-muted));
  }
  &:focus {
    border-color: var(--focus-ring);
  }
`;

const Groups = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  overflow-y: auto;
  min-height: 0;
`;

const Group = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const GroupLabel = styled.span`
  padding: 6px 6px 0;
  font-size: 11px;
  font-weight: var(--font-weight-medium);
  color: var(--text-muted);
`;

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 0 6px 4px;
`;

// The same gold chip the dubbed transcript renders a tag as (design system,
// "Delivery Marker Chip"): a tag is a creative control, so gold, never green.
const TagChip = styled.button`
  display: inline-flex;
  align-items: center;
  padding: 3px 8px;
  border-radius: 6px;
  border: 1px solid color-mix(in srgb, var(--accent) 35%, transparent);
  background: color-mix(in srgb, var(--accent) 14%, transparent);
  color: var(--accent);
  font-family: var(--font-primary);
  font-size: 11px;
  font-weight: var(--font-weight-medium);
  line-height: 1.3;
  cursor: pointer;
  transition:
    background var(--transition-fast),
    border-color var(--transition-fast);

  &:hover {
    background: color-mix(in srgb, var(--accent) 24%, transparent);
  }
  &:focus-visible {
    outline: none;
    border-color: var(--accent);
    box-shadow: 0 0 0 1px var(--focus-ring);
  }
`;

const EmptyLine = styled.p`
  margin: 0;
  padding: 8px 6px;
  font-size: var(--text-xs);
  color: var(--text-muted);
`;
